import type React from 'react';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import type {
  RouteProps,
  RouteChildrenProps,
  RouteComponentProps,
} from 'react-router-dom';
import { Switch, Route } from 'react-router-dom';

import { useLoginState } from '@xing-com/crate-hooks-use-login-state';

import AuthenticatedRoute from '../components/authenticated-route';
import { Breadcrumbs } from '../components/breadcrumbs';
import { useCompaniesExperiments } from '../hooks/use-companies-experiments/use-companies-experiments';
import { FollowedCompanies } from '../pages/followed-companies';
import { Industries } from '../pages/industries';
import { IndustryByCompanies } from '../pages/industry-by-companies';
import { LoggedOutLandingPage } from '../pages/logged-out-landing-page';
import { ManagedCompanies } from '../pages/managed-companies';
import { MostVisitedCompanies } from '../pages/most-visited-companies';
import { RecentlyClaimedCompanies } from '../pages/recently-claimed-companies';
import { RecommendedCompanies } from '../pages/recommended-companies';
import { getIndustryName } from '../utils/url';
import { ROUTES } from './routes';

export type IndustryUrlParam = { industry: string };
type NameIndustry = ({ industry }: IndustryUrlParam) => string;

type CompaniesRoute = {
  onlyAuthenticated: boolean;
  path: string;
  exact: Pick<RouteProps, 'exact'>['exact'];
  Component: React.FC<RouteChildrenProps>;
  name?: string | NameIndustry;
  hasCrumbs: boolean;
};

export type BreadcrumbsType = Pick<CompaniesRoute, 'name' | 'path'>;

const RedirectFromCompaniesToPages = () => {
  const COMPANIES_PATH = '/companies/';
  const PAGES_PATH = '/pages/';
  const newURL = window.location.href.replace(COMPANIES_PATH, PAGES_PATH);
  useEffect(() => {
    window.location.href = newURL;
  }, [newURL]);

  return null;
};

export const Router: React.FC = () => {
  const { isLoggedIn } = useLoginState();
  const intl = useIntl();
  const { isNewLoggedOutExperimentEnabled } = useCompaniesExperiments();

  const routes: CompaniesRoute[] = [
    {
      onlyAuthenticated: false,
      exact: true,
      path: ROUTES.companies,
      Component: isLoggedIn ? RecommendedCompanies : LoggedOutLandingPage,
      name: isLoggedIn ? 'Recommended Companies' : 'SE_LABEL_COMPANIES',
      hasCrumbs: !isLoggedIn && !isNewLoggedOutExperimentEnabled,
    },
    {
      onlyAuthenticated: true,
      exact: true,
      path: ROUTES.followedCompanies,
      Component: FollowedCompanies,
      hasCrumbs: false,
    },
    {
      onlyAuthenticated: true,
      exact: true,
      path: ROUTES.managedCompanies,
      Component: ManagedCompanies,
      hasCrumbs: false,
    },
    {
      onlyAuthenticated: false,
      exact: true,
      path: ROUTES.mostVisitedCompanies,
      Component: MostVisitedCompanies,
      name: 'CP_100_MOST_CLICKED_COMPANIES',
      hasCrumbs: !isLoggedIn && !isNewLoggedOutExperimentEnabled,
    },
    {
      onlyAuthenticated: false,
      exact: true,
      path: ROUTES.recentlyClaimedCompanies,
      Component: RecentlyClaimedCompanies,
      name: 'CP_NEW_EMPLOYERS',
      hasCrumbs: !isLoggedIn,
    },
    {
      onlyAuthenticated: false,
      exact: true,
      path: ROUTES.industries,
      Component: Industries,
      name: 'CP_200_INDUSTRIES',
      hasCrumbs: !isLoggedIn && !isNewLoggedOutExperimentEnabled,
    },
    {
      onlyAuthenticated: false,
      exact: false,
      path: ROUTES.industry,
      Component: IndustryByCompanies as $TSFixMe,
      name: ({ industry }) => getIndustryName({ intl, id: industry }),
      hasCrumbs: !isLoggedIn && !isNewLoggedOutExperimentEnabled,
    },
  ];

  return (
    <Switch>
      {routes.map(
        ({ onlyAuthenticated, exact, path, Component, hasCrumbs }) => {
          const RouteComponent = onlyAuthenticated ? AuthenticatedRoute : Route;

          return (
            <RouteComponent
              key={path}
              exact={exact}
              path={path}
              render={(props: RouteComponentProps) => (
                <>
                  {hasCrumbs && <Breadcrumbs routes={routes} {...props} />}
                  <Component {...props} />
                </>
              )}
            />
          );
        }
      )}
      <Route path="/companies/*">
        <RedirectFromCompaniesToPages />
      </Route>
      {/* <Miss /> */}
    </Switch>
  );
};
